<script setup lang="ts">
  import type { Editor } from '@tiptap/vue-3';
  import { computed } from 'vue';

  import { Button } from '@ui/components/button';
  import ColorGrid from '@ui/components/ColorGrid.vue';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { Separator } from '@ui/components/separator';
  import { Toggle } from '@ui/components/toggle';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import FontFamilySelect from './FontFamilySelect.vue';
  import FontSizeSelect from './FontSizeSelect.vue';

  type TextAlignment = 'left' | 'center' | 'right';

  interface Props {
    currNumAttachments?: number;
    // Bytes already attached. Only used when `maxTotalSize` is set.
    currTotalSize?: number;
    editor?: Editor | null;
    innerToolbarClass?: string;
    maxNumAttachments?: number;
    // When set, the attach button is gated by combined byte size instead of file count.
    maxTotalSize?: number;
    openFileInput?: () => void;
    toggleClass?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    currNumAttachments: 0,
    currTotalSize: 0,
    maxNumAttachments: 15,
  });

  const emits = defineEmits<{
    (e: 'openFileInput'): void;
  }>();

  const allowedNumAttachments = computed<number>(() => {
    return props.maxNumAttachments - props.currNumAttachments;
  });

  const attachDisabled = computed<boolean>(() => {
    if (props.maxTotalSize != null) {
      return props.currTotalSize >= props.maxTotalSize;
    }
    return allowedNumAttachments.value <= 0;
  });

  const setLink = () => {
    if (!props.editor) return;
    const previousUrl = props.editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) return;

    // empty
    if (!url) {
      props.editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      props.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const textAlignment = computed<TextAlignment>(() => {
    if (props.editor?.isActive({ textAlign: 'center' })) return 'center';
    if (props.editor?.isActive({ textAlign: 'right' })) return 'right';

    return 'left';
  });
</script>

<template>
  <div class="flex flex-row">
    <div class="flex flex-row gap-1 px-0! items-center" :class="innerToolbarClass">
      <FontFamilySelect v-if="editor" :editor="editor" :toggle-class="toggleClass" />
      <FontSizeSelect v-if="editor" :editor="editor" :toggle-class="toggleClass" />
      <Separator class="h-[20px] my-auto mx-0" orientation="vertical" />

      <Tooltip>
        <TooltipTrigger as-child>
          <Toggle
            aria-label="Toggle bold"
            size="sm"
            :data-state="editor?.isActive('bold') ? 'on' : 'off'"
            :class="toggleClass"
            @click="editor?.chain().focus().toggleBold().run()"
          >
            <i class="fa fa-bold" aria-hidden="true" />
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side="bottom"> Bold (⌘B) </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger as-child>
          <Toggle
            size="sm"
            aria-label="Toggle italic"
            :data-state="editor?.isActive('italic') ? 'on' : 'off'"
            :class="toggleClass"
            @click="editor?.chain().focus().toggleItalic().run()"
          >
            <i class="fa fa-italic" aria-hidden="true" />
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side="bottom"> Italic (⌘I) </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger as-child>
          <Toggle
            size="sm"
            aria-label="Toggle underline"
            :data-state="editor?.isActive('underline') ? 'on' : 'off'"
            :class="toggleClass"
            @click="editor?.chain().focus().toggleUnderline().run()"
          >
            <i class="fa fa-underline" aria-hidden="true" />
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side="bottom"> Underline (⌘U) </TooltipContent>
      </Tooltip>

      <Popover>
        <PopoverTrigger as-child>
          <Button :class="toggleClass" size="icon-sm" type="button" variant="ghost">
            <i
              class="fa far fa-palette"
              :style="{
                borderColor: editor?.getAttributes('textStyle').color || '#000000',
              }"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-fit p-2" side="top">
          <ColorGrid
            :value="editor?.getAttributes('textStyle').color"
            @color-selected="(color) => editor?.chain().focus().setColor(color).run()"
          />
        </PopoverContent>
      </Popover>

      <!--    <Tooltip> -->
      <!--      <TooltipTrigger as-child> -->
      <!--        <div> -->
      <!--          <select class="ql-background" /> -->
      <!--        </div> -->
      <!--      </TooltipTrigger> -->
      <!--      <TooltipContent side="bottom"> Background Color </TooltipContent> -->
      <!--    </Tooltip> -->

      <!--        <Separator class="h-[20px] my-auto mx-0" orientation="vertical" /> -->

      <!--          <Tooltip> -->
      <!--            <TooltipTrigger as-child> -->
      <!--              <DropdownMenu> -->
      <!--                <DropdownMenuTrigger as-child> -->
      <!--                  <Button variant="ghost" size="icon"> -->
      <!--                    <i class="fa" -->
      <!--                       :class="`fa-align-${textAlignment}`"/> -->
      <!--                  </Button> -->
      <!--                </DropdownMenuTrigger> -->

      <!--                <DropdownMenuContent class="min-w-fit"> -->
      <!--                  <DropdownMenuGroup> -->
      <!--                    <DropdownMenuItem @click="editor.chain().focus().setTextAlign('left').run()"> -->
      <!--                      <i class="fa fa-align-left"/> -->
      <!--                    </DropdownMenuItem> -->

      <!--                    <DropdownMenuItem @click="editor.chain().focus().setTextAlign('center').run()"> -->
      <!--                      <i class="fa fa-align-center"/> -->
      <!--                    </DropdownMenuItem> -->

      <!--                    <DropdownMenuItem @click="editor.chain().focus().setTextAlign('right').run()"> -->
      <!--                      <i class="fa fa-align-right"/> -->
      <!--                    </DropdownMenuItem> -->
      <!--                  </DropdownMenuGroup> -->
      <!--                </DropdownMenuContent> -->
      <!--              </DropdownMenu> -->
      <!--            </TooltipTrigger> -->

      <!--            <TooltipContent side="bottom"> Align </TooltipContent> -->
      <!--          </Tooltip> -->

      <Tooltip>
        <TooltipTrigger as-child>
          <Toggle
            :data-state="editor?.isActive('bulletList') ? 'on' : 'off'"
            :class="toggleClass"
            size="sm"
            @click="editor?.chain().focus().toggleBulletList().run()"
          >
            <i class="fa fa-list-ul" aria-hidden="true" />
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side="bottom"> Bulleted List </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger as-child>
          <Toggle
            :data-state="editor?.isActive('orderedList') ? 'on' : 'off'"
            :class="toggleClass"
            size="sm"
            @click="editor?.chain().focus().toggleOrderedList().run()"
          >
            <i class="fa fa-list-ol" aria-hidden="true" />
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side="bottom"> Numbered List </TooltipContent>
      </Tooltip>

      <!--        <Separator class="h-[20px] my-auto mx-0" orientation="vertical" /> -->

      <Tooltip>
        <TooltipTrigger as-child>
          <Toggle
            :data-state="editor?.isActive('link') ? 'on' : 'off'"
            :class="toggleClass"
            size="sm"
            @click="setLink"
          >
            <i class="fa far fa-link-simple" />
          </Toggle>
        </TooltipTrigger>
        <TooltipContent side="bottom"> Insert Link (⌘K) </TooltipContent>
      </Tooltip>

      <Popover>
        <PopoverTrigger as-child>
          <Toggle
            :data-state="editor?.isActive('table') ? 'on' : 'off'"
            :class="toggleClass"
            size="sm"
            aria-label="Table"
          >
            <i class="fa far fa-table" />
          </Toggle>
        </PopoverTrigger>
        <PopoverContent class="w-fit p-1" side="bottom" align="start">
          <div v-if="!editor?.isActive('table')" class="flex flex-col">
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded"
              @click="
                editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
              "
            >
              <i class="far fa-plus text-2xs" />
              Insert table
            </button>
          </div>
          <div v-else class="flex flex-col min-w-[180px]">
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded"
              @click="editor?.chain().focus().addRowBefore().run()"
            >
              <i class="far fa-arrow-up text-2xs" />
              Add row above
            </button>
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded"
              @click="editor?.chain().focus().addRowAfter().run()"
            >
              <i class="far fa-arrow-down text-2xs" />
              Add row below
            </button>
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded"
              @click="editor?.chain().focus().addColumnBefore().run()"
            >
              <i class="far fa-arrow-left text-2xs" />
              Add column left
            </button>
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded"
              @click="editor?.chain().focus().addColumnAfter().run()"
            >
              <i class="far fa-arrow-right text-2xs" />
              Add column right
            </button>
            <Separator class="my-1" />
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded"
              @click="editor?.chain().focus().toggleHeaderRow().run()"
            >
              <i class="far fa-heading text-2xs" />
              Toggle header row
            </button>
            <Separator class="my-1" />
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded text-destructive"
              @click="editor?.chain().focus().deleteRow().run()"
            >
              <i class="far fa-trash text-2xs" />
              Delete row
            </button>
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded text-destructive"
              @click="editor?.chain().focus().deleteColumn().run()"
            >
              <i class="far fa-trash text-2xs" />
              Delete column
            </button>
            <button
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted rounded text-destructive"
              @click="editor?.chain().focus().deleteTable().run()"
            >
              <i class="far fa-trash text-2xs" />
              Delete table
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Tooltip v-if="!!openFileInput">
        <TooltipTrigger as-child>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            :disabled="attachDisabled"
            @click="openFileInput"
          >
            <i class="fa far fa-paperclip-vertical" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom"> Attach Files </TooltipContent>
      </Tooltip>

      <slot name="default" />

      <!--    <Separator -->
      <!--      v-if="slots.extras" -->
      <!--      class="h-[20px] my-auto mx-0" -->
      <!--      orientation="vertical" -->
      <!--    /> -->

      <slot name="extra_buttons" />
    </div>

    <slot name="extras" />
  </div>
</template>
